import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { Button, IconButton } from '@mui/material';
import {
  KeyboardArrowDown as ArrowDropDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { OrderedSet } from 'immutable';
import { BREAK_LINE_DELIMITER, SMS_IDENTITY_PROVIDER } from '@src/constants';
import {
  CONVERTING_STATUS,
  DIRECTION,
  MAX_CHARACTER_PER_BLOCK,
} from '@src/constants/project';
import { ContentState, Editor, EditorState, Modifier } from 'draft-js';
import Tooltip from '@src/components/Tooltip';
import BadgeAvatar from '@src/components/BadgeAvatar';
import { COLOR } from '@src/styles/color';
import addBlock from '@src/assets/icons/add-block.svg';
import {
  BREAK_TIME_REGEX,
  DEFAULT_BREAK_TIME,
  DEFAULT_SPEED,
  VOICE_LEVEL,
  VOICE_OWNERSHIP,
} from '@src/constants/voice';
import { v4 as uuidv4 } from 'uuid';
import VoicesDialog from '@src/components/VoicesDialog';
import { countTextLength } from '@src/services/tts';
import BreakTimeHighlight from '@src/containers/TTS/BreakTimeHightLight';
import { generateDecorator } from '@src/services/editor';
import actions from '@src/redux/actions';
import apis from '@src/apis';
import { downloadFileByUrl, getFileName } from '@src/utils/file';
import { LANDING_PAGE_URL } from '@src/configs';
import { VC_VOICE_CODE_REGEX } from '@src/constants/voiceCloning';
import { CATEGORY_TYPE, PLATFORM_TYPE } from '@src/constants/support';
import { isBlockChanged } from '@src/services/project';
import {
  StyledAddNewBlockZone,
  StyledBlockEditor,
  StyledContentBlock,
  StyledContentBlockWrapper,
} from './index.style';
import MoreOptionBlock from './MoreOptionBlock';
import SynthesisButton from './SynthesisButton';
import ConfigOptionBlock from './ConfigOptionBlock';
import CreditsCount from './CreditsCount';

const ContentBlock = ({
  blockId,
  block,
  characterExceed,
  onAddNewParagraphs,
  onSelectParagraphId,
  isLastBlock,
  isFocusing,
  onFocusingBlock,
  onDragEnd,
  disabledMoveUp,
  onSaveProject,
  voiceDefault,
  isPlayingBlock,
  isDownloading,
  onOpenOutOfCreditsDialog,
  onOpenWrongSyntaxDialog,
  onOpenWrongSyntaxDialogV2,
  onOpenDiscardedVoiceCloningDialog,
  checkValidCreateSynthesis,
  isHighlight,
  handlePlayAudio,
  projectId,
  originalBlock,
  onConvertBlockToAudio,
  audioType,
  isPlaying,
  isDisabledDeleteBlock,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const inputRef = useRef(null);
  const { identityProvider, id: userId } = useSelector(
    (state) => state.auth.user,
  );
  const { filterVoice } = useSelector((state) => state.voice);
  const dispatch = useDispatch();
  const isSMSUser = useMemo(
    () => Object.values(SMS_IDENTITY_PROVIDER).includes(identityProvider),
    [identityProvider],
  );

  const {
    voiceCode = voiceDefault?.code,
    text = '',
    speed = DEFAULT_SPEED,
    breakTime = DEFAULT_BREAK_TIME,
    audioLink,
    status,
    requestId,
    showSuccessIcon,
    audioExpiredAt,
    voice = voiceDefault,
    isBlockEditting,
  } = block;

  const exceedBlockMaxChar = countTextLength(text) > MAX_CHARACTER_PER_BLOCK;
  const isSynthesizing =
    isDownloading || status === CONVERTING_STATUS.IN_PROGRESS;
  const hasSuccessSynthesized =
    audioLink && status === CONVERTING_STATUS.SUCCESS;
  const isAudioExpired = audioExpiredAt && moment().isAfter(audioExpiredAt);
  const [content, setContent] = useState(
    EditorState.createEmpty(
      generateDecorator(BREAK_TIME_REGEX, BreakTimeHighlight),
    ),
  );
  const [openVoices, setOpenVoices] = useState(false);
  const [openPopperBlockOption, setOpenPopperBlockOption] = useState(false);

  const handleOpenVoices = () => {
    const voiceOwnershipFilter = filterVoice?.voiceOwnership;
    const isClonedVoice = VC_VOICE_CODE_REGEX.test(voice?.code);
    let voiceOwnership = isClonedVoice
      ? VOICE_OWNERSHIP.PERSONAL
      : VOICE_OWNERSHIP.VBEE;

    if (
      isClonedVoice &&
      (voiceOwnershipFilter === VOICE_OWNERSHIP.COMMUNITY ||
        voice?.userId !== userId)
    )
      voiceOwnership = VOICE_OWNERSHIP.COMMUNITY;

    if (!voiceOwnershipFilter || voiceOwnershipFilter !== voiceOwnership)
      dispatch(
        actions.voice.changeFilterVoice({
          ...filterVoice,
          voiceOwnership,
        }),
      );
    setOpenVoices(true);
  };
  const handleCloseVoices = () => setOpenVoices(false);

  const handleOpenDeleteDialog = () => {
    onSelectParagraphId(blockId);
  };
  const isCheckBlockChanged = useMemo(() => {
    const withoutExtension = block?.audioLink?.split('.')?.pop();
    const currentBlock = {
      text: block?.text,
      voiceCode: block?.voiceCode,
      speed: block?.speed,
      audioType: withoutExtension,
    };
    const blockOriginal = {
      text: originalBlock?.text,
      voiceCode: originalBlock?.voiceCode,
      speed: originalBlock?.speed,
      audioType,
    };

    return isBlockChanged(currentBlock, blockOriginal);
  }, [block, originalBlock, audioType]);

  const isDisableConvertBtn = useMemo(
    () =>
      !text ||
      countTextLength(text) > MAX_CHARACTER_PER_BLOCK ||
      (!isCheckBlockChanged && hasSuccessSynthesized && !isAudioExpired),
    [text, audioLink, status, isCheckBlockChanged, isAudioExpired],
  );

  const isDisablePlayAudio = useMemo(
    () => isCheckBlockChanged || !hasSuccessSynthesized || isAudioExpired,
    [isCheckBlockChanged, hasSuccessSynthesized, isAudioExpired],
  );

  const handleChangeContent = (newContent) => setContent(newContent);

  const handleChangeVoice = (newVoice) => {
    dispatch(
      actions.project.updateBlock(blockId, {
        voiceCode: newVoice?.code,
        voice: {
          code: newVoice?.code,
          name: newVoice?.name,
          roundImage: newVoice?.roundImage,
          language: {
            roundImage: newVoice?.language?.roundImage,
          },
          level: newVoice?.level,
          provider: newVoice?.provider,
          eolDate: newVoice?.eolDate,
          creditFactor: newVoice?.creditFactor,
          canonicalVoice: newVoice?.canonicalVoice,
          discardAt: newVoice?.discardAt,
          retentionDays: newVoice?.retentionDays,
          status: newVoice?.status,
          userId: newVoice?.userId,
        },
      }),
    );
  };

  const handleChangeSpeed = (field, value) => {
    dispatch(actions.project.updateBlock(blockId, { speed: value }));
  };

  const handleChangeBreakTime = (value) => {
    const currentContent = content.getCurrentContent();
    const selection = content.getSelection();
    const label = `<break time=${value}s/>`;
    const textWithEntity = Modifier.insertText(
      currentContent,
      selection,
      label,
      OrderedSet.of('BOLD'),
    );

    dispatch(
      actions.project.updateBlock(blockId, {
        breakTime: value,
        text: textWithEntity.getPlainText(),
      }),
    );
    handleChangeContent(
      EditorState.push(content, textWithEntity, 'insert-characters'),
    );
  };

  const handleChangeText = (value) => {
    dispatch(actions.project.updateBlock(blockId, { text: value }));
  };

  const handleEditorChange = (editorState) => {
    const selectionState = editorState.getSelection();
    if (!selectionState.toJS().hasFocus) return;
    const contentState = editorState.getCurrentContent();
    const plainText = contentState.getPlainText('\n');
    let newContent = editorState;

    const isContentChanged = !content
      .getCurrentContent()
      .equals(editorState.getCurrentContent());
    if (isContentChanged) {
      const currentStyle = editorState.getCurrentInlineStyle();
      if (currentStyle && currentStyle.size) {
        newContent = EditorState.setInlineStyleOverride(
          editorState,
          OrderedSet.of(),
        );
      }
      handleChangeContent(newContent);
      handleChangeText(plainText);
    } else {
      handleChangeContent(editorState);
    }
  };

  const handleAddNewParagraphs = () => {
    onAddNewParagraphs(blockId);
  };

  const handlePastedText = (txt) => {
    const splitParagraphs = txt.split(BREAK_LINE_DELIMITER);
    if (splitParagraphs.length > 1) {
      const selectionState = content.getSelection();
      const currentContent = content.getCurrentContent();

      // Insert pasted text at cursor position (or replace selected text)
      const textWithEntity = Modifier.replaceText(
        currentContent,
        selectionState,
        txt,
      );

      // Get the updated text with the pasted content
      const updatedText = textWithEntity.getPlainText('\n');
      const paragraphs = updatedText
        .split(BREAK_LINE_DELIMITER)
        .filter((p) => p.trim());

      // First paragraph is a combination of text before cursor + first part of pasted text
      const firstParagraphText = paragraphs.shift();

      // Create new blocks for the remaining paragraphs plus the text that was after the cursor
      const newBlocks = [];

      // Process middle paragraphs (if any)
      while (paragraphs.length > 1) {
        newBlocks.push({
          voice,
          voiceCode,
          speed,
          breakTime,
          id: uuidv4(),
          text: paragraphs.shift(),
        });
      }

      // Last paragraph combines the last part of pasted text with text after cursor
      if (paragraphs.length === 1) {
        newBlocks.push({
          voice,
          voiceCode,
          speed,
          breakTime,
          id: uuidv4(),
          text: paragraphs[0],
        });
      }

      // Update the editor state
      handleChangeContent(
        EditorState.createWithContent(
          ContentState.createFromText(firstParagraphText),
        ),
      );

      // Update current block with first paragraph text
      dispatch(
        actions.project.updateBlock(blockId, {
          text: firstParagraphText,
          voiceCode,
          speed,
          breakTime,
        }),
      );

      // Add new paragraphs
      if (newBlocks.length > 0) {
        onAddNewParagraphs(blockId, newBlocks);
      }
      inputRef.current?.blur();
      return true;
    }
    return false;
  };

  const handleFocusOnBlock = () => {
    onFocusingBlock(blockId);
    inputRef.current?.focus();
  };

  const handleDragUp = () => {
    if (!disabledMoveUp && !isPlaying) {
      onDragEnd(blockId, DIRECTION.UP);
    }
  };

  const handleDragDown = () => {
    if (!isLastBlock && !isPlaying) {
      onDragEnd(blockId, DIRECTION.DOWN);
    }
  };

  const handleDownloadBlockAudio = async () => {
    if (hasSuccessSynthesized) {
      if (block?.isSample) {
        const downloadAudioName = getFileName(audioLink);
        downloadFileByUrl(audioLink, downloadAudioName);
        return;
      }
      const res = await apis.requests.getAudioDownloadUrl(requestId);
      if (res?.status) {
        const downloadAudioLink = res.result?.audio;
        const downloadAudioName = getFileName(audioLink);
        downloadFileByUrl(downloadAudioLink, downloadAudioName);
      }
    }
  };

  const handleOpenErrorReport = () => {
    const ticketMetadata = {
      requestId,
      voiceCode,
      userId,
      blockId,
      projectId,
    };
    const base64TicketMetadata = btoa(JSON.stringify(ticketMetadata));
    const encodedTicketMetadata = encodeURIComponent(base64TicketMetadata);

    window.open(
      `${LANDING_PAGE_URL}/support?metadata=${encodedTicketMetadata}&platform=${PLATFORM_TYPE.DESKTOP_WEB}&category=${CATEGORY_TYPE.PROJECT}`,
      '_blank',
    );
  };

  useEffect(() => {
    if (text) {
      const contentEditor = EditorState.createWithContent(
        ContentState.createFromText(text),
        generateDecorator(BREAK_TIME_REGEX, BreakTimeHighlight),
      );
      handleChangeContent(contentEditor);
    }
  }, []);

  useEffect(() => {
    if (isFocusing) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isFocusing]);

  const renderConfigBlock = () => (
    <ConfigOptionBlock
      handleOpenVoices={handleOpenVoices}
      handleChangeSpeed={handleChangeSpeed}
      handleChangeBreakTime={handleChangeBreakTime}
      voice={voice}
      speed={speed}
      breakTime={breakTime}
      isSynthesizing={isSynthesizing}
    />
  );
  const renderCharacterCount = () => (
    <CreditsCount
      isAdvancedVoice={
        voice?.level === VOICE_LEVEL.ADVANCED && voice?.creditFactor > 1
      }
      creditFactor={voice?.creditFactor}
      exceedBlockMaxCredits={exceedBlockMaxChar}
      credits={countTextLength(text)}
      maxCredits={MAX_CHARACTER_PER_BLOCK}
      exceedBlockMaxCreditsDescription={t('exceedBlockMaxChar', {
        maxCredits: MAX_CHARACTER_PER_BLOCK.toLocaleString(language),
      })}
    />
  );

  const renderDrag = () => (
    <div className="drag">
      <div
        onClick={handleDragUp}
        disabled={disabledMoveUp || isPlaying}
        role="button"
        tabIndex="-1"
        className={classNames('drag-icon-wrapper', {
          'disabled-move': disabledMoveUp || isPlaying,
        })}
      >
        <ArrowUpIcon style={{ color: COLOR.secondary['64'] }} />
      </div>
      <div
        onClick={handleDragDown}
        disabled={isLastBlock || isPlaying}
        role="button"
        tabIndex="-1"
        className={classNames('drag-icon-wrapper', {
          'disabled-move': isLastBlock || isPlaying,
        })}
      >
        <ArrowDropDownIcon style={{ color: COLOR.secondary['64'] }} />
      </div>
    </div>
  );

  const renderVoice = () => (
    <div className="voice">
      <Tooltip arrow title={t('chooseVoice')} placement="top">
        <IconButton
          disabled={isSynthesizing}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenVoices();
          }}
        >
          <BadgeAvatar
            width={25}
            smallImgWidth={10}
            img={voice?.roundImage}
            smallImg={voice?.language?.roundImage}
            type="image"
          />
        </IconButton>
      </Tooltip>
    </div>
  );

  return (
    <StyledContentBlockWrapper>
      <StyledContentBlock
        isLastParagraphId={isLastBlock}
        isFocusingOnBlock={isFocusing}
        isError={exceedBlockMaxChar}
        isHighlight={isHighlight}
      >
        {renderDrag()}
        <div className="block-wrapper">
          <div className="block-toolbar-wrapper">
            {renderConfigBlock()}
            {renderCharacterCount()}
          </div>
          <div className="editor-wrapper">
            <StyledBlockEditor
              onClick={handleFocusOnBlock}
              role="button"
              tabIndex="-1"
              isFocusingOnBlock={isFocusing}
            >
              {renderVoice()}
              <div
                className={classNames('editor-container', {
                  'character-exceed': characterExceed,
                })}
              >
                <Editor
                  placeholder={t('enterTextHere')}
                  editorState={content}
                  handlePastedText={handlePastedText}
                  stripPastedStyles
                  onChange={handleEditorChange}
                  ref={inputRef}
                  readOnly={isSynthesizing}
                />
              </div>
            </StyledBlockEditor>
            <div className="editor-toolbar">
              <SynthesisButton
                blockId={blockId}
                status={status}
                isPlaying={isPlayingBlock}
                isDisableConvertBtn={isDisableConvertBtn}
                onSaveProject={onSaveProject}
                showSuccessIcon={showSuccessIcon}
                audioExpiredAt={audioExpiredAt}
                onOpenOutOfCreditsDialog={onOpenOutOfCreditsDialog}
                onOpenWrongSyntaxDialog={onOpenWrongSyntaxDialog}
                onOpenWrongSyntaxDialogV2={onOpenWrongSyntaxDialogV2}
                checkValidCreateSynthesis={checkValidCreateSynthesis}
                handlePlayAudio={handlePlayAudio}
                onOpenDiscardedVoiceCloningDialog={
                  onOpenDiscardedVoiceCloningDialog
                }
                isDisablePlayAudio={isDisablePlayAudio}
                onConvertBlockToAudio={onConvertBlockToAudio}
                isBlockEditting={isBlockEditting}
              />
              <MoreOptionBlock
                openMoreOptionBlock={openPopperBlockOption}
                onOpenDeleteDialog={handleOpenDeleteDialog}
                onOpenMoreOptionBlock={setOpenPopperBlockOption}
                onOpenReportDialog={handleOpenErrorReport}
                isDisabledDownloadBlockAudio={isDisablePlayAudio}
                onDownloadBlockAudio={handleDownloadBlockAudio}
                isDisabledDeleteBlock={isDisabledDeleteBlock}
              />
            </div>
          </div>
        </div>
        {openVoices && (
          <VoicesDialog
            open={openVoices}
            activeVoiceId={voice && voice.id}
            activeVoiceCode={voice && voice.code}
            onClose={handleCloseVoices}
            onChangeVoice={handleChangeVoice}
            hasLanguageFilter={!isSMSUser}
          />
        )}
      </StyledContentBlock>
      {!isLastBlock && (
        <StyledAddNewBlockZone>
          <div className="zone-wrapper">
            <hr className="border" />
            <Button
              onClick={handleAddNewParagraphs}
              className="add-btn"
              variant="contained"
              startIcon={
                <img className="add-btn-icon" src={addBlock} alt="icon" />
              }
            >
              {t('addMoreBlock')}
            </Button>
            <hr className="border" />
          </div>
        </StyledAddNewBlockZone>
      )}
    </StyledContentBlockWrapper>
  );
};

function arePropsEqual(prevProps, nextProps) {
  return (
    prevProps.blockId === nextProps.blockId &&
    prevProps.characterExceed === nextProps.characterExceed &&
    prevProps.isLastBlock === nextProps.isLastBlock &&
    prevProps.isFocusing === nextProps.isFocusing &&
    prevProps.block === nextProps.block &&
    prevProps.disabledMoveUp === nextProps.disabledMoveUp &&
    prevProps.voiceDefault === nextProps.voiceDefault &&
    prevProps.listVoice === nextProps.listVoice &&
    prevProps.isPlaying === nextProps.isPlaying &&
    prevProps.isDownloading === nextProps.isDownloading &&
    prevProps.isHighlight === nextProps.isHighlight &&
    prevProps.projectId === nextProps.projectId &&
    prevProps.isPlayingBlock === nextProps.isPlayingBlock &&
    prevProps.originalBlock === nextProps.originalBlock &&
    prevProps.audioType === nextProps.audioType &&
    prevProps.isDisabledDeleteBlock === nextProps.isDisabledDeleteBlock
  );
}

export default memo(ContentBlock, arePropsEqual);
